// src/pages/MovieToolsLanding.tsx — public marketing landing for the
// Movie Tools downloadable desktop app. Lives at /movietools and is the
// default route when accessed via movietools.snowbirdindustries.us.

import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const GH_OWNER = 'Snowbirdjj81';
const GH_REPO = 'movietools';
const RELEASES_API = `https://api.github.com/repos/${GH_OWNER}/${GH_REPO}/releases/latest`;
const RELEASES_PAGE = `https://github.com/${GH_OWNER}/${GH_REPO}/releases/latest`;

type OS = 'mac' | 'win' | 'linux' | 'unknown';

interface ReleaseAsset {
  name: string;
  browser_download_url: string;
  size: number;
}
interface Release {
  tag_name: string;
  name: string;
  published_at: string;
  assets: ReleaseAsset[];
}

function detectOS(): OS {
  if (typeof navigator === 'undefined') return 'unknown';
  const ua = navigator.userAgent.toLowerCase();
  const platform = (navigator.platform || '').toLowerCase();
  if (/mac|iphone|ipad/.test(platform) || /macintosh|mac os x/.test(ua)) return 'mac';
  if (/win/.test(platform) || /windows/.test(ua)) return 'win';
  if (/linux/.test(platform) || /linux/.test(ua)) return 'linux';
  return 'unknown';
}

function pickAssetForOS(release: Release | null, os: OS): ReleaseAsset | null {
  if (!release) return null;
  const assets = release.assets || [];
  const find = (re: RegExp) => assets.find((a) => re.test(a.name));
  if (os === 'mac') {
    // Prefer Apple Silicon if the user is on ARM Mac
    const isArm = navigator.userAgent.includes('Mac') && /arm|aarch/i.test(navigator.userAgent);
    return (isArm && find(/arm64.*\.dmg$/i)) || find(/\.dmg$/i) || null;
  }
  if (os === 'win') return find(/\.exe$/i);
  if (os === 'linux') return find(/\.AppImage$/i) || find(/\.deb$/i);
  return null;
}

const FEATURES = [
  { icon: '🎬', title: 'Bundled ffmpeg', body: 'No separate install. The app ships with ffmpeg-static and uses it locally on your machine.' },
  { icon: '📁', title: 'Folder concat',  body: 'Drop in a folder of MKVs and Movie Tools merges them into a single MP4 named after the folder.' },
  { icon: '👀', title: 'Auto-watch',     body: 'Point it at a Movies folder. New rips are queued and converted automatically.' },
  { icon: '⚡', title: 'Smart remux',    body: 'Video is copied without re-encoding when possible. Audio is normalized to AAC for compatibility.' },
  { icon: '🔒', title: 'Local-first',    body: 'Files never leave your machine. Snowbird Industries credits gate access; conversion runs on your hardware.' },
  { icon: '🖥️', title: 'Cross-platform', body: 'Native installers for macOS (Apple Silicon + Intel), Windows, and Linux.' },
];

const MovieToolsLanding: React.FC = () => {
  const { isAuthenticated } = useAuth();
  const [release, setRelease] = useState<Release | null>(null);
  const [releaseError, setReleaseError] = useState(false);
  const [os] = useState<OS>(() => detectOS());

  useEffect(() => {
    fetch(RELEASES_API)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((data: Release) => setRelease(data))
      .catch(() => setReleaseError(true));
  }, []);

  const primary = pickAssetForOS(release, os);
  const otherAssets = release?.assets.filter((a) => a !== primary) || [];

  const osLabel = os === 'mac' ? 'macOS' : os === 'win' ? 'Windows' : os === 'linux' ? 'Linux' : 'your platform';

  return (
    <div style={{ minHeight: 'calc(100vh - 200px)', background: '#fafbfc' }}>
      {/* Hero */}
      <section style={hero}>
        <div style={heroInner}>
          <div style={heroText}>
            <span style={badge}>For SI Members</span>
            <h1 style={heroTitle}>Movie Tools</h1>
            <p style={heroLead}>
              The fastest way to convert your MKV rips into a clean MP4 library.
              Local conversion on your own machine — no upload, no waiting on a server.
            </p>

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem', marginTop: '1.5rem' }}>
              {primary ? (
                <a href={primary.browser_download_url} style={primaryBtn}>
                  ⬇ Download for {osLabel}
                  <span style={{ display: 'block', fontSize: 12, opacity: 0.8, marginTop: 2 }}>
                    {primary.name} · {(primary.size / 1024 / 1024).toFixed(1)} MB
                  </span>
                </a>
              ) : releaseError ? (
                <a href={RELEASES_PAGE} target="_blank" rel="noreferrer" style={primaryBtn}>
                  ⬇ View releases on GitHub
                </a>
              ) : (
                <button style={primaryBtnDisabled} disabled>Loading latest release…</button>
              )}
              <a href={RELEASES_PAGE} target="_blank" rel="noreferrer" style={secondaryBtn}>
                All platforms & versions
              </a>
            </div>

            {release && (
              <div style={{ fontSize: 12, color: '#6b7280', marginTop: '0.75rem' }}>
                Version {release.tag_name} · published {new Date(release.published_at).toLocaleDateString()}
              </div>
            )}

            {!isAuthenticated && (
              <div style={signInCallout}>
                <strong>Don't have an SI account yet?</strong>{' '}
                Movie Tools requires a free Snowbird Industries account to launch.{' '}
                <Link to="/signup" style={inlineLink}>Sign up</Link> or{' '}
                <Link to="/login" style={inlineLink}>sign in</Link>.
              </div>
            )}
          </div>

          <div style={heroVisual}>
            <div style={mockWindow}>
              <div style={mockTitleBar}>
                <span style={dot('#ef4444')} />
                <span style={dot('#f59e0b')} />
                <span style={dot('#10b981')} />
                <span style={{ marginLeft: '0.75rem', fontSize: 11, color: '#6b7280' }}>Movie Tools — Queue</span>
              </div>
              <div style={{ padding: '1rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                <MockJob status="running" name="Inception (2010).mkv" pct={62} />
                <MockJob status="pending" name="The Matrix Trilogy/" />
                <MockJob status="done"    name="Blade Runner 2049.mkv" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section style={section}>
        <h2 style={h2}>What it does</h2>
        <div style={featureGrid}>
          {FEATURES.map((f) => (
            <div key={f.title} style={featureCard}>
              <div style={featureIcon}>{f.icon}</div>
              <h3 style={featureTitle}>{f.title}</h3>
              <p style={featureBody}>{f.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Pricing */}
      <section style={{ ...section, background: '#fff' }}>
        <h2 style={h2}>Credits</h2>
        <p style={sectionLead}>
          Movie Tools is a Snowbird Industries app — every launch checks your credit balance.
          Conversion happens locally on your machine; credits cover access to the app itself.
        </p>
        <div style={pricingGrid}>
          <div style={pricingCard}>
            <div style={pricingTier}>Per launch</div>
            <div style={pricingPrice}>1 credit</div>
            <div style={pricingNote}>Each session opens a new credit window. Session lasts as long as the app is running.</div>
          </div>
          <div style={pricingCard}>
            <div style={pricingTier}>Subscription</div>
            <div style={pricingPrice}>Included</div>
            <div style={pricingNote}>Standard / Premium / Admin tier subscribers don't pay per-launch. <Link to="/credits" style={inlineLink}>See plans</Link></div>
          </div>
        </div>
      </section>

      {/* Other downloads */}
      {otherAssets.length > 0 && (
        <section style={{ ...section, background: '#fafbfc' }}>
          <h2 style={h2}>Other downloads</h2>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
            {otherAssets
              .filter((a) => /\.(dmg|exe|AppImage|deb)$/i.test(a.name))
              .map((a) => (
                <a key={a.name} href={a.browser_download_url} style={tertiaryBtn}>
                  {a.name} ({(a.size / 1024 / 1024).toFixed(1)} MB)
                </a>
              ))}
          </div>
        </section>
      )}
    </div>
  );
};

// ── Subcomponents ─────────────────────────────────────────────────

const MockJob: React.FC<{ status: 'running' | 'pending' | 'done'; name: string; pct?: number }> = ({ status, name, pct }) => {
  const color = status === 'done' ? '#10b981' : status === 'running' ? '#3b82f6' : '#9ca3af';
  return (
    <div style={{ background: '#f8fafc', border: '1px solid #e5e7eb', borderRadius: 6, padding: '0.5rem 0.75rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: 12 }}>
        <span style={{ background: color, color: '#fff', padding: '1px 6px', borderRadius: 3, fontSize: 10, textTransform: 'uppercase' }}>{status}</span>
        <span style={{ flex: 1, fontFamily: 'ui-monospace,monospace', fontSize: 11 }}>{name}</span>
      </div>
      {status === 'running' && pct != null && (
        <div style={{ marginTop: 6, height: 4, background: '#e5e7eb', borderRadius: 2, overflow: 'hidden' }}>
          <div style={{ width: `${pct}%`, height: '100%', background: '#3b82f6' }} />
        </div>
      )}
    </div>
  );
};

const dot = (c: string): React.CSSProperties => ({
  display: 'inline-block', width: 10, height: 10, background: c, borderRadius: 5, marginRight: 6,
});

// ── Styles ────────────────────────────────────────────────────────

const hero: React.CSSProperties = {
  background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
  color: '#fff', padding: '4rem 1.5rem',
};
const heroInner: React.CSSProperties = {
  maxWidth: 1100, margin: '0 auto',
  display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2rem', alignItems: 'center',
};
const heroText: React.CSSProperties = {};
const heroVisual: React.CSSProperties = { display: 'flex', justifyContent: 'center' };
const badge: React.CSSProperties = {
  background: 'rgba(59,130,246,0.15)', color: '#93c5fd',
  padding: '0.25rem 0.75rem', borderRadius: 999, fontSize: 12, fontWeight: 600,
  letterSpacing: 0.5, textTransform: 'uppercase',
};
const heroTitle: React.CSSProperties = {
  fontSize: '3.5rem', margin: '1rem 0 1rem 0', fontWeight: 700, lineHeight: 1.1,
};
const heroLead: React.CSSProperties = { fontSize: 18, color: '#cbd5e1', lineHeight: 1.5, maxWidth: 480 };
const signInCallout: React.CSSProperties = {
  marginTop: '1.5rem', padding: '0.75rem 1rem',
  background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)',
  borderRadius: 8, fontSize: 13, color: '#cbd5e1',
};
const inlineLink: React.CSSProperties = { color: '#60a5fa', textDecoration: 'none', fontWeight: 500 };
const mockWindow: React.CSSProperties = {
  width: 380, background: '#fff', borderRadius: 10, overflow: 'hidden',
  boxShadow: '0 25px 50px -12px rgba(0,0,0,0.5)', color: '#1f2937',
};
const mockTitleBar: React.CSSProperties = {
  display: 'flex', alignItems: 'center', padding: '0.5rem 0.75rem',
  background: '#f3f4f6', borderBottom: '1px solid #e5e7eb',
};

const section: React.CSSProperties = {
  padding: '4rem 1.5rem', maxWidth: 1100, margin: '0 auto',
};
const h2: React.CSSProperties = { fontSize: 28, fontWeight: 700, marginBottom: '1rem', color: '#0f172a' };
const sectionLead: React.CSSProperties = { color: '#475569', maxWidth: 700, marginBottom: '2rem', lineHeight: 1.5 };
const featureGrid: React.CSSProperties = {
  display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1.25rem',
};
const featureCard: React.CSSProperties = {
  background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10,
  padding: '1.25rem',
};
const featureIcon: React.CSSProperties = { fontSize: 32, marginBottom: '0.5rem' };
const featureTitle: React.CSSProperties = { fontSize: 17, fontWeight: 600, margin: '0 0 0.5rem 0', color: '#0f172a' };
const featureBody: React.CSSProperties = { fontSize: 14, color: '#475569', lineHeight: 1.5, margin: 0 };
const pricingGrid: React.CSSProperties = {
  display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1rem',
};
const pricingCard: React.CSSProperties = {
  background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10,
  padding: '1.5rem',
};
const pricingTier: React.CSSProperties = {
  fontSize: 12, fontWeight: 600, textTransform: 'uppercase', color: '#64748b', letterSpacing: 0.5,
};
const pricingPrice: React.CSSProperties = { fontSize: 28, fontWeight: 700, color: '#0f172a', margin: '0.5rem 0' };
const pricingNote: React.CSSProperties = { fontSize: 13, color: '#475569', lineHeight: 1.5 };

const primaryBtn: React.CSSProperties = {
  display: 'inline-block', padding: '0.85rem 1.5rem', background: '#3b82f6', color: '#fff',
  borderRadius: 8, fontSize: 16, fontWeight: 600, textDecoration: 'none', textAlign: 'center',
  border: 'none', cursor: 'pointer',
};
const primaryBtnDisabled: React.CSSProperties = { ...primaryBtn, background: '#475569', cursor: 'wait' };
const secondaryBtn: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', padding: '0.85rem 1.25rem',
  background: 'transparent', color: '#cbd5e1',
  border: '1px solid rgba(255,255,255,0.2)', borderRadius: 8,
  fontSize: 14, textDecoration: 'none',
};
const tertiaryBtn: React.CSSProperties = {
  display: 'inline-block', padding: '0.5rem 0.85rem',
  background: '#fff', color: '#1f2937', border: '1px solid #d1d5db',
  borderRadius: 6, fontSize: 13, textDecoration: 'none',
  fontFamily: 'ui-monospace,monospace',
};

export default MovieToolsLanding;
